// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `viewports` route body (PR-C, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `viewports` route projector: the RICH-raw per-VIEWPORT entity record (document-wide — NO
// `?viewportEntityId`), PAGINATED by the offsetKey cursor, SORTED by a `key:dir` grammar, FIELD-
// projected by `?fields`, SPACE-SCOPED by the seam's resolved space (it filters `snapshot.viewports`
// to `resolvedSpaceId`, and the seam neutralizes the facet to `[]` under an unknown `?scope` — like
// `entities`). A BODY only — the envelope / cursor shape / `?scope` resolution / value validation live
// in the seam; registered via `registerRouteProjector('viewports', projectViewports)`.
//
// KEYED by the DOC-UNIQUE entity `id` (NOT `viewportId`). The RAW `viewportId` (group 69) is the only
// classifier — NO synthesized `kind` (the `viewportId===1`=paper-space-control convention is documented
// FOR THE AGENT but never derived into the contract; the F3 round-trip-preserve trap — never infer a
// semantic field from an incidental value).
//
// DEFAULT order (no `?sort`): the DETERMINISTIC `(spaceId, viewportId, id)` chain — also the stable
// FINAL tie-break for an explicit `?sort` (so a continuation pages identically across an equal-primary
// run). An explicit `?sort` overrides the PRIMARY key but keeps the chain as the tie-break.
//
// The UNBOUNDED per-VP lists (`frozenLayers`/`frozenLayerHandles`/`layerOverrideHandles`) are NOT inlined
// on the row — the `viewports` route projects only their DERIVED COUNTS (`frozenLayersCount`/
// `frozenLayerHandlesCount`/`layerOverrideHandlesCount`); the full lists live in the nested drill-down
// routes. `frozenLayers` (XDATA names) and `frozenLayerHandles` (native group-331 handles) are PARALLEL
// UNRECONCILED freeze representations (model.ts:1517-1519) — BOTH counts are projected so an agent sees
// there ARE native 331 handles even when the XDATA names are empty (ПРИОРИТЕТ #1 — no silent truncation).
//
// routeRevisions: `{ geometryRevision, tableRevision }` — viewports are ENTITIES (geometry-derived
// state) whose table metadata (the layer table the frozen-layer/override handles reference) is table-
// derived. `snapshotRevision` rides as the page ANCHOR only (INV-9).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers. depcruise cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type RouteProjector,
  type SnapshotViewportRef,
} from "../sceneSummaryProjector";
import { compareFacetsBy, finishPage, parseKeyDirSort } from "./projectionHelpers";

/**
 * The LEAN CORE field set projected when `?fields` is ABSENT (byte economy — the FULL rich set is
 * reached via `?fields`/`?fullField`). Every member is allowlisted. Includes the two DERIVED counts
 * (the unbounded lists are NOT inlined — only their counts).
 */
const LEAN_CORE_FIELDS = [
  "id",
  "viewportId",
  "spaceId",
  "center",
  "width",
  "height",
  "displayLocked",
  "layer",
  "frozenLayersCount",
  "frozenLayerHandlesCount",
  "layerOverrideHandlesCount",
] as const satisfies readonly (
  | keyof SnapshotViewportRef
  | "frozenLayersCount"
  | "frozenLayerHandlesCount"
  | "layerOverrideHandlesCount"
)[];

/** The legal `key:dir` sort KEYS (the natural keys; the deterministic chain is the tie-break). */
const VIEWPORT_SORT_KEYS = ["id", "spaceId", "viewportId"] as const;
type ViewportSortKey = (typeof VIEWPORT_SORT_KEYS)[number];

/**
 * The DETERMINISTIC ORDER + stable tie-break chain: `(spaceId, viewportId, id)`. Passed to
 * `compareFacetsBy` as the tie-break chain so an explicit `?sort` resolves equal-primary ties by it;
 * also the comparator for the DEFAULT (no-`?sort`) order. `spaceId`/`id` are strings; `viewportId` is
 * an optional number (absent sorts first, deterministically — `compareValues` orders undefined).
 */
const VIEWPORT_ORDER_CHAIN = ["spaceId", "viewportId", "id"] as const;

/** A viewport row + the three DERIVED counts (the projected row — the rich facet plus the list counts). */
type ViewportRow = SnapshotViewportRef & {
  readonly frozenLayersCount: number;
  readonly frozenLayerHandlesCount: number;
  readonly layerOverrideHandlesCount: number;
};
/** The union of a real sort key and the chain keys the comparator extractor may be asked for. */
type ViewportOrderKey = ViewportSortKey | (typeof VIEWPORT_ORDER_CHAIN)[number];

/**
 * Extract a viewport row's value for a sort / tie-break key. `id`/`spaceId` are RAW strings;
 * `viewportId` is the RAW optional number (group 69 — absent ⇒ undefined, sorts first). The seam
 * validated the primary key is allowlisted; the chain keys are a fixed subset.
 */
function viewportSortValue(row: ViewportRow, key: ViewportOrderKey): string | number | undefined {
  switch (key) {
    case "id":
      return row.id;
    case "spaceId":
      return row.spaceId;
    case "viewportId":
      return row.viewportId;
    default:
      return undefined;
  }
}

/**
 * The DEFAULT (no-`?sort`) comparator: the deterministic `(spaceId, viewportId, id)` chain ASC. Built
 * on the SHARED `compareFacetsBy` with `spaceId` as the primary + `(viewportId, id)` as the tie-break,
 * so the default order is ONE truth with the explicit-sort tie-break (no cloned compare logic).
 */
function defaultOrderCompare(a: ViewportRow, b: ViewportRow): number {
  return compareFacetsBy(a, b, "spaceId", "asc", viewportSortValue, VIEWPORT_ORDER_CHAIN);
}

/**
 * The `viewports` route body. scope-filter → attach derived counts → sort (default `(spaceId,
 * viewportId, id)` or explicit `?sort` with the chain as tie-break) → (fullField | field-project) →
 * paginate. Binds `routeRevisions` to `{ geometryRevision, tableRevision }`. `snapshotRevision` rides
 * as the page ANCHOR only. byteTruncated: false (the companion serializer owns byte-budget truncation).
 */
export const projectViewports: RouteProjector = ({ query, cursor, snapshot, resolvedSpaceId }) => {
  // SCOPE — the seam already resolved `?scope` (and neutralized an unknown scope to `viewports:[]`).
  // `null` → all viewports (document scope); else filter to the resolved space.
  const scopedViewports: readonly SnapshotViewportRef[] =
    resolvedSpaceId === null
      ? snapshot.viewports
      : snapshot.viewports.filter((v) => v.spaceId === resolvedSpaceId);

  // ATTACH the three DERIVED counts (the unbounded lists are NOT inlined — only their counts ride on the
  // `viewports` row; the full lists live in the nested drill-down routes). The lists stay on the row so
  // the counts derive from them, but `frozenLayers`/`frozenLayerHandles`/`layerOverrideHandles` are NOT in
  // the allowlist, so `projectFacetFields` never emits them here (INV-1). BOTH `frozenLayerHandles` (native
  // group-331 freeze) and `layerOverrideHandles` (groups 335/343/344/91 property overrides) are OPTIONAL on
  // the schema (model.ts:1520/:1526; absent ⇒ count 0 — `?? 0`); only `frozenLayers` (XDATA names) is a
  // `.default([])` array always present. `frozenLayerHandles` is PARALLEL to and UNRECONCILED with the XDATA
  // `frozenLayers` names — all three counts are projected (ПРИОРИТЕТ #1).
  const rows: ViewportRow[] = scopedViewports.map((v) => ({
    ...v,
    frozenLayersCount: v.frozenLayers.length,
    frozenLayerHandlesCount: v.frozenLayerHandles?.length ?? 0,
    layerOverrideHandlesCount: v.layerOverrideHandles?.length ?? 0,
  }));

  // SORT (before pagination — cursor-determinism). A COPY; no `?sort` → the DEFAULT `(spaceId,
  // viewportId, id)` chain; an explicit `?sort` overrides the PRIMARY but keeps the chain as the
  // deterministic tie-break (so an equal-primary run pages identically across a continuation).
  const parsedSort = parseKeyDirSort<ViewportSortKey>(query.sort, VIEWPORT_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? [...rows].sort(defaultOrderCompare)
      : [...rows].sort((a, b) =>
          compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, viewportSortValue, VIEWPORT_ORDER_CHAIN),
        );

  // PROJECTION-MODE + PAGINATION + FIELD-PROJECTION + ENVELOPE — the SHARED route-body tail (`finishPage`).
  // ROUTE-SPECIFIC binding ([L7] / fullstack:965): viewports are ENTITIES (geometry-derived state →
  // geometryRevision); their table metadata (the layer table the frozen/override handles reference) is
  // table-derived (tableRevision). `snapshotRevision` is EXCLUDED (it bumps on every commit → false-stale);
  // it rides as the page ANCHOR only.
  return finishPage(ordered, query, cursor, LEAN_CORE_FIELDS, {
    routeRevisions: {
      geometryRevision: snapshot.revisions.geometryRevision,
      tableRevision: snapshot.revisions.tableRevision,
    },
    resolvedSpaceId,
    snapshotRevision: snapshot.snapshotRevision,
    projectionVersion: snapshot.projectionVersion,
  });
};
